<template>
  <section v-show="active" class="app-modal__section">
    <header class="app-modal__section-head">
      <h4>{{ saleGeneralText.header.title }}</h4>
      <p>{{ saleGeneralText.header.description }}</p>
    </header>

    <div class="app-modal__grid two-columns">
      <div class="sale-code-name-row full-width">
        <label v-if="showCodeField" class="app-modal-field sale-code-name-row__code">
          <span>{{ saleGeneralText.fields.code }}</span>
          <input
            v-model.trim="form.code"
            type="text"
            class="form-control"
            :placeholder="saleGeneralText.placeholders.code"
          />
        </label>

        <label
          class="app-modal-field"
          :class="showCodeField ? 'sale-code-name-row__name' : 'sale-code-name-row__name-full'"
        >
          <span>{{ saleGeneralText.fields.nameRequired }}</span>
          <input
            v-model.trim="form.saleName"
            data-focus-field="saleName"
            type="text"
            class="form-control"
            :class="{ 'is-invalid': hasFieldError('saleName') }"
            :placeholder="saleGeneralText.placeholders.name"
            @blur="touchField('saleName')"
          />
          <div class="invalid-feedback d-block" v-if="hasFieldError('saleName')">
            <span v-if="v$.form.saleName.$errors.length">
              {{ v$.form.saleName.$errors[0].$message }}
            </span>
          </div>
        </label>
      </div>

      <SaleModalSingleCustomerField
        v-if="!isMultiCustomerMode"
        :form="form"
        :v$="v$"
      />

      <SaleModalMultiCustomerField
        v-else-if="!isEditMode"
        :form="form"
        :v$="v$"
      />

      <div class="app-modal-field full-width">
        <span>{{ saleGeneralText.fields.serviceRequired }}</span>
        <multiselect
          v-model="form.service"
          :options="options.services"
          data-focus-field="service"
          track-by="value"
          label="label"
          :show-labels="false"
          :searchable="true"
          :allow-empty="true"
          :placeholder="saleGeneralText.placeholders.service"
          :class="['app-modal-select', { 'is-invalid': hasFieldError('service') }]"
          @close="touchField('service')"
        >
          <template #noResult>
            <span>{{ salesModalText.common.noOption }}</span>
          </template>
          <template #noOptions>
            <span>{{ salesModalText.common.noOption }}</span>
          </template>
        </multiselect>
        <div class="invalid-feedback d-block" v-if="hasFieldError('service')">
          <span v-if="v$.form.service.$errors.length">
            {{ v$.form.service.$errors[0].$message }}
          </span>
        </div>
      </div>

      <template v-if="!isFlatRateMode">
        <div class="app-modal-field">
          <span>{{ saleGeneralText.fields.saleDate }}</span>
          <VueDatePicker
            v-model="form.saleDate"
            class="app-modal-datepicker"
            menu-class-name="app-modal-datepicker-menu"
            :placeholder="saleGeneralText.placeholders.date"
            :enable-time-picker="false"
            auto-apply
            locale="es-MX"
            format="dd/MM/yyyy"
            hide-input-icon
            :show-now-button="true"
            :now-button-label="saleGeneralText.placeholders.today"
          />
        </div>

        <div class="app-modal-field">
          <span>{{ saleGeneralText.fields.deliveryDate }}</span>
          <VueDatePicker
            v-model="form.deliveryDate"
            class="app-modal-datepicker"
            menu-class-name="app-modal-datepicker-menu"
            :placeholder="saleGeneralText.placeholders.date"
            :enable-time-picker="false"
            auto-apply
            locale="es-MX"
            format="dd/MM/yyyy"
            hide-input-icon
            :show-now-button="true"
            :now-button-label="saleGeneralText.placeholders.today"
          />
        </div>
      </template>
    </div>

    <section class="sale-amounts-box">
      <header class="sale-amounts-box__head">
        <div>
          <h4>{{ saleGeneralText.amounts.title }}</h4>
          <p>{{ saleGeneralText.amounts.description }}</p>
        </div>

        <AppCurrencyControl
          v-model="currencyModel"
          :exchange-rate="form.exchangeRate"
          @update:exchange-rate="form.exchangeRate = $event"
        />
      </header>

      <div class="sale-amounts-grid">
        <label v-if="!isFlatRateMode" class="app-modal-field">
          <span>{{ saleGeneralText.fields.subtotal }}</span>
          <AppMoneyInput
            v-model="form.subtotal"
            output-mode="number"
            :decimal-places="2"
            :minimum-value="0"
            :currency="currencyModel"
          />
        </label>

        <label v-if="!isFlatRateMode" class="app-modal-field">
          <span>{{ saleGeneralText.fields.budgetGeneral }}</span>
          <AppMoneyInput
            v-model="form.budgetGeneral"
            output-mode="number"
            :decimal-places="2"
            :minimum-value="0"
            :currency="currencyModel"
          />
        </label>

        <template v-if="isAdvertisingEnabled && !isFlatRateMode">
          <label class="app-modal-field">
            <span>{{ saleGeneralText.fields.mediaAmount }}</span>
            <AppMoneyInput
              v-model="form.mediaAmount"
              output-mode="number"
              :decimal-places="2"
              :minimum-value="0"
              :currency="currencyModel"
            />
          </label>

          <label class="app-modal-field">
            <span>{{ saleGeneralText.fields.agencyAmount }}</span>
            <AppMoneyInput
              v-model="form.agencyAmount"
              output-mode="number"
              :decimal-places="2"
              :minimum-value="0"
              :currency="currencyModel"
              :readonly="true"
            />
          </label>
        </template>
      </div>

      <div v-if="isFlatRateMode" class="app-modal__grid two-columns sale-plan-voucher-row">
        <label class="app-modal-field">
          <span>{{ saleGeneralText.fields.budgetGeneral }}</span>
          <AppMoneyInput
            v-model="form.budgetGeneral"
            output-mode="number"
            :decimal-places="2"
            :minimum-value="0"
            :currency="currencyModel"
          />
        </label>

        <div class="app-modal-field">
          <span>{{ flatRatePlanText.voucherType.label }}</span>
          <multiselect
            v-model="voucherTypeModel"
            :options="options.documentTypes"
            track-by="value"
            label="label"
            :show-labels="false"
            :searchable="false"
            :allow-empty="false"
            class="app-modal-select"
            :placeholder="flatRatePlanText.voucherType.placeholder"
          >
            <template #noResult>
              <span>{{ flatRatePlanText.common.noOption }}</span>
            </template>
            <template #noOptions>
              <span>{{ flatRatePlanText.common.noOption }}</span>
            </template>
          </multiselect>
        </div>
      </div>

      <SaleModalFlatRatePaymentPlanBox
        v-if="isFlatRateMode"
        :form="form"
        :options="options"
        :is-edit-mode="isEditMode"
        :first-installment-options="firstInstallmentOptions"
        :tax-preview="taxPreview"
        :v$="v$"
      />
    </section>

    <div v-if="isFlatRateMode" class="app-modal__grid payment-plan-schedule-grid sale-flat-rate-schedule-row">
      <div class="app-modal-field">
        <span>{{ flatRatePlanText.schedule.startDateLabel }}</span>
        <VueDatePicker
          v-model="form.startDate"
          class="app-modal-datepicker"
          menu-class-name="app-modal-datepicker-menu"
          :placeholder="flatRatePlanText.schedule.datePlaceholder"
          :enable-time-picker="false"
          auto-apply
          locale="es-MX"
          format="dd/MM/yyyy"
          hide-input-icon
          :show-now-button="true"
          :now-button-label="flatRatePlanText.schedule.todayButton"
        />
      </div>

      <div v-if="showInvoiceDate" class="app-modal-field">
        <span>{{ flatRatePlanText.schedule.invoiceDateLabel }}</span>
        <VueDatePicker
          v-model="form.cfdiDate"
          class="app-modal-datepicker"
          menu-class-name="app-modal-datepicker-menu"
          :placeholder="flatRatePlanText.schedule.datePlaceholder"
          :enable-time-picker="false"
          auto-apply
          locale="es-MX"
          format="dd/MM/yyyy"
          hide-input-icon
          :show-now-button="true"
          :now-button-label="flatRatePlanText.schedule.todayButton"
        />
      </div>

      <div class="app-modal-field">
        <span>{{ flatRatePlanText.schedule.paymentDateLabel }}</span>
        <VueDatePicker
          v-model="form.paymentDate"
          class="app-modal-datepicker"
          menu-class-name="app-modal-datepicker-menu"
          :placeholder="flatRatePlanText.schedule.datePlaceholder"
          :enable-time-picker="false"
          auto-apply
          locale="es-MX"
          format="dd/MM/yyyy"
          hide-input-icon
          :show-now-button="true"
          :now-button-label="flatRatePlanText.schedule.todayButton"
        />
      </div>
    </div>
  </section>
</template>

<script>
import VueDatePicker from '@vuepic/vue-datepicker';
import AppMoneyInput from '@/components/common/AppMoneyInput.vue';
import AppCurrencyControl from '@/components/common/AppCurrencyControl.vue';
import SaleModalSingleCustomerField from '@/components/sales/modal/SaleModalSections/SaleModalSingleCustomerField.vue';
import SaleModalMultiCustomerField from '@/components/salesMultiCustomer/modal/SaleMultiCustomerModalSections/SaleModalMultiCustomerField.vue';
import SaleModalFlatRatePaymentPlanBox from '@/components/sales/modal/SaleModalSections/SaleModalFlatRatePaymentPlanBox.vue';
import { resolveExpenseFeeVoucherFlags } from '@/composables/expensesFees/shared/useExpenseFeeTaxFlow';
import { SALES_TEXT } from '@/texts/catalog/sales';
import { FLAT_RATES_TEXT } from '@/texts/catalog/flatRates';

const FLAT_RATE_PLAN_TEXT = FLAT_RATES_TEXT.modal.flatRatePlan;
const SALES_MODAL_TEXT = SALES_TEXT.modal;
const SALE_GENERAL_TEXT = SALES_MODAL_TEXT.content.general;

export default {
  name: 'SaleModalSectionGeneral',
  components: {
    VueDatePicker,
    AppMoneyInput,
    AppCurrencyControl,
    SaleModalSingleCustomerField,
    SaleModalMultiCustomerField,
    SaleModalFlatRatePaymentPlanBox,
  },
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    form: {
      type: Object,
      required: true,
    },
    options: {
      type: Object,
      required: true,
    },
    showCodeField: {
      type: Boolean,
      default: true,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
    isAdvertisingEnabled: {
      type: Boolean,
      default: true,
    },
    customerMode: {
      type: String,
      default: 'single',
    },
    isFlatRateMode: {
      type: Boolean,
      default: false,
    },
    firstInstallmentOptions: {
      type: Array,
      default: () => [],
    },
    taxPreview: {
      type: Object,
      default: () => ({}),
    },
    v$: {
      type: Object,
      default: null,
    },
  },
  emits: [],
  computed: {
    salesModalText() {
      return SALES_MODAL_TEXT;
    },
    saleGeneralText() {
      return SALE_GENERAL_TEXT;
    },
    isMultiCustomerMode() {
      return String(this.customerMode || '').trim().toLowerCase() === 'multi';
    },
    currencyModel: {
      get() {
        const current = String(this.form?.currencyCode || '').trim().toUpperCase();
        return current !== '' ? current : 'MXN';
      },
      set(value) {
        this.form.currencyCode = String(value || '').trim().toUpperCase() || 'MXN';
      },
    },
    flatRatePlanText() {
      return FLAT_RATE_PLAN_TEXT;
    },
    voucherTypeModel: {
      get() {
        const target = String(this.form?.voucherType || 'cfdi').trim().toLowerCase();
        return (Array.isArray(this.options?.documentTypes) ? this.options.documentTypes : [])
          .find((option) => String(option?.value || '').trim().toLowerCase() === target)
          || this.options?.documentTypes?.[0]
          || null;
      },
      set(option) {
        this.form.voucherType = String(option?.value || 'cfdi').trim().toLowerCase();
      },
    },
    showInvoiceDate() {
      const voucherType = String(this.form?.voucherType || '').trim().toLowerCase();
      return !resolveExpenseFeeVoucherFlags(voucherType).isSimpleNoteVoucher;
    },
  },
  methods: {
    hasFieldError(fieldName) {
      if (!this.v$ || !this.v$.form || !this.v$.form[fieldName]) {
        return false;
      }

      return Boolean(this.v$.form[fieldName].$error);
    },
    touchField(fieldName) {
      if (!this.v$ || !this.v$.form || !this.v$.form[fieldName]) {
        return;
      }

      this.v$.form[fieldName].$touch();
    },
  },
};
</script>

<style scoped>
.sale-code-name-row {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 10px;
  align-items: start;
  width: 100%;
}

.sale-code-name-row.full-width {
  grid-column: 1 / -1;
}

.sale-code-name-row__code {
  grid-column: span 2;
  min-width: 0;
}

.sale-code-name-row__code input {
  text-transform: uppercase;
}

.sale-code-name-row__name {
  grid-column: span 10;
  width: 100%;
  min-width: 0;
}

.sale-code-name-row__name-full {
  grid-column: 1 / -1;
  width: 100%;
  min-width: 0;
}

.sale-code-name-row__name .form-control {
  width: 100%;
}

.sale-amounts-box {
  margin-top: 12px;
  border: 1px solid rgba(153, 152, 153, 0.2);
  border-radius: 10px;
  padding: 12px;
  display: grid;
  gap: 12px;
  width: 100%;
}

.sale-amounts-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.sale-plan-voucher-row {
  margin-top: 2px;
}

.sale-flat-rate-schedule-row {
  margin-top: 8px;
}

.payment-plan-schedule-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.sale-amounts-box__head h4 {
  margin: 0;
  font-size: 14px;
  color: #1f2937;
}

.sale-amounts-box__head p {
  margin: 4px 0 0;
  font-size: 12px;
  color: #6b7280;
}

.sale-amounts-box__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.app-modal-select {
  width: 100%;
}

@media (max-width: 900px) {
  .sale-amounts-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .payment-plan-schedule-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .sale-code-name-row {
    grid-template-columns: minmax(0, 1fr);
  }

  .sale-amounts-box__head {
    flex-direction: column;
  }

}
</style>
